'use client'

import {
  Badge,
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@repo/ui'
import { AlertTriangle } from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  useAgentAttendanceSettings,
  useUpsertAgentAttendanceSettings,
} from '@/hooks/use-agents'
import type { AgentTone, EscalationStrategy } from '@/types/agents'
import type { Member } from '@/types/inbox'

interface SettingsTabProps {
  tone: AgentTone
  isActive: boolean
  isDefault: boolean
  waitForCadence: boolean
  agentId: string
  orgId: string | null
  members: Member[]
  channels: { id: string; name: string; default_agent_id?: string | null }[]
  departments: { id: string; name: string; defaultAgentId?: string | null }[]
  onChange: (key: string, value: unknown) => void
}

const TONE_LABELS: Record<AgentTone, string> = {
  FRIENDLY: 'Amigável',
  FORMAL: 'Formal',
  CONSULTIVE: 'Consultivo',
  DIRECT: 'Direto',
}

const STRATEGY_LABELS: Record<EscalationStrategy, string> = {
  QUEUE: 'Fila (sem atribuir)',
  SPECIFIC_OPERATOR: 'Operador fixo',
  LEAD_OWNER: 'Dono do lead',
  ROUND_ROBIN: 'Rodízio',
  MANAGER_DECIDES: 'Gerente decide',
}

const KEEP_CURRENT_DEPARTMENT = '__current__'

function memberLabel(member: Member) {
  return member.name ?? member.email
}

function HandoffAssignmentSection({
  agentId,
  orgId,
  members,
  departments,
}: {
  agentId: string
  orgId: string | null
  members: Member[]
  departments: { id: string; name: string }[]
}) {
  const { data: settings } = useAgentAttendanceSettings(orgId, agentId)
  const upsert = useUpsertAgentAttendanceSettings(orgId, agentId)

  const [strategy, setStrategy] = useState<EscalationStrategy>('QUEUE')
  const [departmentId, setDepartmentId] = useState<string | null>(null)
  const [operatorId, setOperatorId] = useState<string | null>(null)
  const [poolIds, setPoolIds] = useState<string[]>([])

  // Semeia o estado local quando as settings chegam.
  useEffect(() => {
    if (!settings) return
    setStrategy(settings.escalationStrategy ?? 'QUEUE')
    setDepartmentId(settings.escalationDepartmentId ?? null)
    setOperatorId(settings.specificOperatorUserId ?? null)
    setPoolIds(settings.roundRobinUserIds ?? [])
  }, [settings])

  function persist(overrides: {
    strategy?: EscalationStrategy
    departmentId?: string | null
    operatorId?: string | null
    poolIds?: string[]
  }) {
    const next = {
      escalationStrategy: overrides.strategy ?? strategy,
      escalationDepartmentId:
        overrides.departmentId !== undefined
          ? overrides.departmentId
          : departmentId,
      specificOperatorUserId:
        overrides.operatorId !== undefined ? overrides.operatorId : operatorId,
      roundRobinUserIds: overrides.poolIds ?? poolIds,
    }
    void upsert.mutateAsync(next)
  }

  return (
    <section>
      <h3 className="text-sm font-semibold">Atribuição no handoff</h3>
      <p className="text-muted-foreground mt-1 text-xs">
        Define para qual setor e responsável o atendimento é encaminhado quando
        a IA passa para um humano.
      </p>
      <div className="border-border bg-card mt-4 space-y-4 rounded-lg border p-4">
        <div className="space-y-2">
          <Label>Setor de destino</Label>
          <Select
            value={departmentId ?? KEEP_CURRENT_DEPARTMENT}
            onValueChange={(value) => {
              const next = value === KEEP_CURRENT_DEPARTMENT ? null : value
              setDepartmentId(next)
              persist({ departmentId: next })
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={KEEP_CURRENT_DEPARTMENT}>
                Manter setor atual
              </SelectItem>
              {departments.map((dept) => (
                <SelectItem key={dept.id} value={dept.id}>
                  {dept.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Estratégia de responsável</Label>
          <Select
            value={strategy}
            onValueChange={(value) => {
              const next = value as EscalationStrategy
              setStrategy(next)
              persist({ strategy: next })
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(STRATEGY_LABELS) as EscalationStrategy[]).map(
                (value) => (
                  <SelectItem key={value} value={value}>
                    {STRATEGY_LABELS[value]}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
        </div>

        {strategy === 'SPECIFIC_OPERATOR' && (
          <div className="space-y-2">
            <Label>Operador fixo</Label>
            <Select
              value={operatorId ?? ''}
              onValueChange={(value) => {
                setOperatorId(value)
                persist({ operatorId: value })
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione um operador" />
              </SelectTrigger>
              <SelectContent>
                {members.map((member) => (
                  <SelectItem key={member.id} value={member.id}>
                    {memberLabel(member)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {strategy === 'ROUND_ROBIN' && (
          <div className="space-y-2">
            <Label>Pool de rodízio</Label>
            <p className="text-muted-foreground text-xs">
              Se vazio, usa os membros do setor de destino.
            </p>
            <div className="max-h-48 space-y-2 overflow-y-auto">
              {members.map((member) => {
                const checked = poolIds.includes(member.id)
                return (
                  <label
                    key={member.id}
                    className="flex items-center gap-2 text-sm"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(value) => {
                        const next = value
                          ? [...poolIds, member.id]
                          : poolIds.filter((id) => id !== member.id)
                        setPoolIds(next)
                        persist({ poolIds: next })
                      }}
                    />
                    {memberLabel(member)}
                  </label>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

function ToggleRow({
  label,
  description,
  checked,
  onCheckedChange,
}: {
  label: string
  description: string
  checked: boolean
  onCheckedChange: (value: boolean) => void
}) {
  return (
    <div className="border-border flex items-center justify-between gap-4 border-b py-4 last:border-0">
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-muted-foreground mt-0.5 text-xs">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  )
}

export function SettingsTab({
  tone,
  isActive,
  isDefault,
  waitForCadence,
  agentId,
  orgId,
  members,
  channels,
  departments,
  onChange,
}: SettingsTabProps) {
  const activeChannels = channels.filter(
    (channel) => channel.default_agent_id === agentId
  )
  const activeDepartments = departments.filter(
    (department) => department.defaultAgentId === agentId
  )
  const isActiveAnywhere =
    isDefault || activeChannels.length > 0 || activeDepartments.length > 0

  return (
    <div className="flex flex-col gap-8">
      <section>
        <h3 className="text-sm font-semibold">Comportamento geral</h3>
        <p className="text-muted-foreground mt-1 text-xs">
          Configurações que afetam o funcionamento global do agente.
        </p>
        <div className="border-border bg-card mt-4 overflow-hidden rounded-lg border">
          <div className="border-border space-y-2 border-b p-4">
            <Label>Tom de comunicação</Label>
            <p className="text-muted-foreground text-xs">
              Define o estilo predominante das respostas, sem substituir as
              instruções do perfil.
            </p>
            <Select
              value={tone}
              onValueChange={(value) => onChange('tone', value as AgentTone)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(TONE_LABELS) as AgentTone[]).map((value) => (
                  <SelectItem key={value} value={value}>
                    {TONE_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="px-4">
            <ToggleRow
              label="Agente habilitado"
              description="Quando desligado, este agente deixa de processar novas mensagens."
              checked={isActive}
              onCheckedChange={(value) => onChange('isActive', value)}
            />
            <ToggleRow
              label="Agente padrão da organização"
              description="Usado como alternativa quando o canal ou setor não possui um agente específico."
              checked={isDefault}
              onCheckedChange={(value) => onChange('isDefault', value)}
            />
            <ToggleRow
              label="Aguardar cadência"
              description="Evita respostas da IA enquanto o lead estiver sendo trabalhado por uma cadência ativa."
              checked={waitForCadence}
              onCheckedChange={(value) => onChange('waitForCadence', value)}
            />
          </div>
        </div>
      </section>

      <HandoffAssignmentSection
        agentId={agentId}
        orgId={orgId}
        members={members}
        departments={departments}
      />

      <section>
        <h3 className="text-sm font-semibold">Onde este agente está ativo</h3>
        <p className="text-muted-foreground mt-1 text-xs">
          Mostra os canais e setores que atualmente encaminham conversas para
          este agente.
        </p>
        {!isActiveAnywhere ? (
          <div className="border-warning/30 bg-warning/10 mt-4 flex items-start gap-3 rounded-lg border px-4 py-3">
            <AlertTriangle className="text-warning mt-0.5 size-4 shrink-0" />
            <div>
              <p className="text-warning text-sm font-medium">
                Agente não está associado a nenhum local
              </p>
              <p className="text-warning mt-0.5 text-xs">
                Configure-o em um canal ou setor, ou marque como agente padrão.
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-4 flex flex-wrap gap-2">
            {isDefault && <Badge>Padrão da organização</Badge>}
            {activeChannels.map((channel) => (
              <Badge key={channel.id} variant="secondary">
                Canal: {channel.name}
              </Badge>
            ))}
            {activeDepartments.map((department) => (
              <Badge key={department.id} variant="secondary">
                Setor: {department.name}
              </Badge>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
